import PropTypes from 'prop-types';
import PillInput, { AtPrefix } from './PillInput';
import { PhoneInputField } from './PhoneInputField';

/**
 * ContactTypeInput — ONE pill input rendered per contact-type config:
 * phone-like types get the embedded PhoneInputField (tel keypad, tel
 * autofill), at-prefix handles get the @ prefix + lowercase/strip, email
 * keeps real email autofill. Extracted from AnonShareBackFlow (device pass
 * 1j) so the CARD share-back wizard and the TEXT share sheet render the
 * same field the same way.
 *
 * onChange receives the VALUE (already prefix-stripped), not the event.
 */
const ContactTypeInput = ({
  typeConfig,
  value,
  onChange,
  onEnter,
  autoFocus = true,
  // Defaults to the type's label. Surfaces that TITLE the field pass '' —
  // a placeholder repeating the title above it is noise (user, 2026-08-16).
  placeholder: placeholderProp = null,
}) => {
  if (!typeConfig) return null;
  const { category, inputMode } = typeConfig;
  const isAtPrefix = category === 'at-prefix';
  const placeholder = placeholderProp === null ? typeConfig.label : placeholderProp;

  if (category === 'phone-like') {
    return (
      <PillInput
        inputSlot={
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              height: '48px',
            }}
          >
            <PhoneInputField
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              autoComplete="tel"
              autoFocus={autoFocus}
              embedded={true}
            />
          </div>
        }
      />
    );
  }

  return (
    <PillInput
      type={inputMode === 'email' ? 'email' : 'text'}
      inputMode={inputMode}
      autoCapitalize="none"
      // Email keeps real email autofill. The handle field must NOT get the
      // contact QuickType (device pass 2: Instagram offered phone/email
      // fills) — Safari ignores autocomplete="off" and infers contact-ness
      // from the form's name/tel siblings, so: an unrecognized token +
      // a neutral name with no trigger words (both documented Safari
      // heuristic-breakers; the contacts icon is hidden via App.css).
      autoComplete={inputMode === 'email' ? 'email' : 'nope'}
      name={inputMode === 'email' ? 'email' : 'handle'}
      value={value}
      onChange={(e) => {
        let val = e.target.value;
        if (isAtPrefix) val = val.replace(/^@/, '').toLowerCase();
        onChange(val);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onEnter) onEnter();
      }}
      placeholder={placeholder}
      prefix={isAtPrefix ? AtPrefix : null}
      autoFocus={autoFocus}
    />
  );
};

ContactTypeInput.propTypes = {
  typeConfig: PropTypes.shape({
    category: PropTypes.string,
    inputMode: PropTypes.string,
    label: PropTypes.string,
  }),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onEnter: PropTypes.func,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default ContactTypeInput;
